import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import returnsApiClient from '../../utilities/api/ReturnsApiClient';
import ReturnOrderBuilder from '../../utilities/returns/ReturnOrderBuilder';
import Styles from './styles.module.css';

const RETURN_MODES = [
    {
        value: 'complete-restock',
        label: 'Komplett abschliessen (auf Lager buchen)',
        description: 'Retoure wird erstellt, genehmigt, empfangen und Ware auf Lager gebucht.'
    },
    {
        value: 'request-approve',
        label: 'Erfassen & genehmigen',
        description: 'Retoure wird erstellt und genehmigt (Status: Angekuendigt). Wareneingang erfolgt spaeter manuell.'
    },
    {
        value: 'request-only',
        label: 'Nur erfassen (nicht genehmigt)',
        description: 'Retoure wird nur erfasst (Status: Angefragt). Genehmigung erfolgt spaeter manuell im ERP.'
    },
    {
        value: 'complete-dispose',
        label: 'Abschliessen mit Abschreibung',
        description: 'Retoure wird komplett abgeschlossen, Ware wird abgeschrieben (nicht auf Lager).'
    }
];

const RETURN_REASONS = [
    { value: 'unknown', label: 'Unbekannt' },
    { value: 'defective', label: 'Defekt' },
    { value: 'unwanted', label: 'Unerwuenscht' },
    { value: 'wrong_item', label: 'Falscher Artikel' },
    { value: 'not_as_described', label: 'Nicht wie beschrieben' },
    { value: 'size_too_large', label: 'Zu gross' },
    { value: 'size_too_small', label: 'Zu klein' },
    { value: 'style', label: 'Stil' },
    { value: 'color', label: 'Farbe' },
    { value: 'other', label: 'Sonstiges' }
];

const ReturnsImport = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [mode, setMode] = useState('complete-restock');
    const [reason, setReason] = useState('unknown');
    const [warehouseId, setWarehouseId] = useState('');
    const [warehouses, setWarehouses] = useState([]);
    const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);

    useEffect(() => {
        loadWarehouses();
    }, []);

    const loadWarehouses = async () => {
        setIsLoadingWarehouses(true);
        try {
            const result = await returnsApiClient.getWarehouses();
            if (result.success && result.data.length > 0) {
                setWarehouses(result.data);
                setWarehouseId(result.data[0].id);
            }
        } catch (error) {
            console.error('[ReturnsImport] Error loading warehouses:', error);
        } finally {
            setIsLoadingWarehouses(false);
        }
    };

    const onDrop = (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0]);
            setImportResults(null);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: '.csv',
        multiple: false
    });

    const handleImport = async () => {
        if (!selectedFile || !warehouseId) return;

        setIsImporting(true);
        setImportResults(null);

        try {
            const results = await ReturnOrderBuilder.processReturnImport(selectedFile, {
                warehouseId,
                reason,
                mode
            });

            setImportResults(results);

            if (results.success) {
                console.log('[ReturnsImport] Import completed:', results);
            }
        } catch (error) {
            console.error('[ReturnsImport] Import failed:', error);
            setImportResults({
                success: false,
                created: 0,
                failed: 0,
                returns: [],
                errors: [error.message]
            });
        } finally {
            setIsImporting(false);
        }
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setImportResults(null);
    };

    const canImport = selectedFile && warehouseId && !isImporting;

    return (
        <div className={Styles.container}>
            <div className={Styles.header}>
                <h1>Retourenimport</h1>
                <p>CSV-Datei mit Retouren hochladen und in Pickware ERP einbuchen</p>
            </div>

            {/* CSV Upload */}
            <div className={Styles.section}>
                <h4>1. CSV-Datei auswaehlen</h4>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 8px 0' }}>
                    Spalten: Bestellnummer | Artikelnummer | Anzahl
                </p>
                <div
                    {...getRootProps()}
                    className={`${Styles.dropzone} ${isDragActive ? Styles.dropzoneActive : ''}`}
                >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                        <p>CSV-Datei hier ablegen...</p>
                    ) : (
                        <p>CSV-Datei hochladen oder per Drag & Drop ablegen</p>
                    )}
                </div>

                {selectedFile && (
                    <div className={Styles.selectedFile}>
                        <div>
                            <span className={Styles.fileName}>{selectedFile.name}</span>
                            <span className={Styles.fileSize}>
                                ({(selectedFile.size / 1024).toFixed(1)} KB)
                            </span>
                        </div>
                        <button className={Styles.removeButton} onClick={handleCancel} type="button">
                            x
                        </button>
                    </div>
                )}
            </div>

            {/* Options */}
            <div className={Styles.section}>
                <h4>2. Optionen</h4>
                <div className={Styles.optionsGrid}>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                            Retourenschritt
                        </label>
                        <select
                            className={Styles.selectField}
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                        >
                            {RETURN_MODES.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                            Lager
                        </label>
                        {isLoadingWarehouses ? (
                            <p className={Styles.loadingText}>Lager werden geladen...</p>
                        ) : (
                            <select
                                className={Styles.selectField}
                                value={warehouseId}
                                onChange={(e) => setWarehouseId(e.target.value)}
                            >
                                {warehouses.map(w => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                            Retourengrund
                        </label>
                        <select
                            className={Styles.selectField}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        >
                            {RETURN_REASONS.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Mode description */}
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 12, fontStyle: 'italic' }}>
                    {RETURN_MODES.find(m => m.value === mode)?.description}
                </p>
            </div>

            {/* Import Button */}
            <div className={Styles.section}>
                <h4>3. Import starten</h4>
                <button
                    className={Styles.importButton}
                    onClick={handleImport}
                    disabled={!canImport}
                >
                    {isImporting ? 'Import laeuft...' : 'Retourenimport starten'}
                </button>
            </div>

            {/* Results */}
            {importResults && (
                <div className={Styles.resultsSection}>
                    <h4>Ergebnisse</h4>

                    {importResults.returns.length > 0 && (
                        <div className={Styles.successStats}>
                            <h5>Erfolgreich erstellt ({importResults.created})</h5>
                            <ul>
                                {importResults.returns.map((ret, i) => (
                                    <li key={i}>
                                        Bestellung {ret.orderNumber} — {ret.itemCount} Artikel
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {importResults.errors.length > 0 && (
                        <div className={Styles.errorStats}>
                            <h5>Fehler ({importResults.errors.length})</h5>
                            <ul>
                                {importResults.errors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReturnsImport;
